/**
 * Returns a standardised inline error message for a required text field.
 *
 * - Empty / whitespace-only value → "{label} is required."
 * - Length over `maxLength` → "{label} is too long. Use {maxLength} characters or fewer."
 * - Otherwise → '' (no error)
 *
 * The label is reused inside the message, so pass the user-facing field name
 * exactly as it appears in the UI (e.g. "Group name", "Key name").
 */
export function validateRequiredText(label: string, value: string, maxLength = 255): string {
  if (!value.trim()) {
    return `${label} is required.`
  }
  return validateMaxLength(label, value, maxLength)
}

/**
 * Returns a standardised inline error message for a non-required text field
 * whose value must not exceed `maxLength` characters.
 *
 * - Length over `maxLength` → "{label} is too long. Use {maxLength} characters or fewer."
 * - Otherwise → '' (no error)
 *
 * Use this when a field is optional (so `validateRequiredText` would falsely
 * flag it as required) but still has a length cap — e.g. Team-member Name,
 * Email, External ID.
 */
export function validateMaxLength(label: string, value: string, maxLength = 255): string {
  if (value.length > maxLength) {
    return `${label} is too long. Use ${maxLength} characters or fewer.`
  }
  return ''
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/**
 * Returns a standardised inline error message for an email field.
 *
 * - Empty / whitespace-only value → '' (caller decides whether it is required)
 * - Value that does not match a basic email pattern → 'Email must be a valid email address.'
 * - Otherwise → '' (no error)
 */
export function validateEmail(value: string): string {
  if (!value.trim()) {
    return ''
  }
  if (!EMAIL_REGEX.test(value.trim())) {
    return 'Email must be a valid email address.'
  }
  return ''
}



// WEBPACK FOOTER //
// ./src/libs/form-validation/index.ts